import { upsertWebPushSubscription } from './notifications.api';

const VAPID_PUBLIC_KEY = import.meta.env['VITE_WEB_PUSH_VAPID_PUBLIC_KEY'] ?? '';

export function getRuntimeTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
}

export function canUseWebPush(): boolean {
  return Boolean(
    VAPID_PUBLIC_KEY &&
      typeof window !== 'undefined' &&
      'Notification' in window &&
      'serviceWorker' in navigator &&
      'PushManager' in window,
  );
}

export async function requestProfileWebPushSubscription(): Promise<
  'subscribed' | 'denied' | 'unsupported' | 'failed'
> {
  if (!canUseWebPush()) return 'unsupported';

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return 'denied';

  try {
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    const subscription =
      existing ??
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToArrayBuffer(VAPID_PUBLIC_KEY),
      }));

    const json = subscription.toJSON();
    if (!json.endpoint || !json.keys?.['p256dh'] || !json.keys?.['auth']) {
      return 'failed';
    }

    await upsertWebPushSubscription({
      endpoint: json.endpoint,
      keys: {
        p256dh: json.keys['p256dh'],
        auth: json.keys['auth'],
      },
      userAgent: navigator.userAgent,
    });

    return 'subscribed';
  } catch {
    return 'failed';
  }
}

function urlBase64ToArrayBuffer(value: string): ArrayBuffer {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replaceAll('-', '+').replaceAll('_', '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return outputArray.buffer;
}
